/**
 * teaserQuestions — MP-03: the ONE source for the conversational teaser.
 *
 * Five pills, each naming a THING being promoted. The pill decides the WORDING of what follows and which
 * visual look the ads are built into. It NEVER decides what data is collected: every pill's answers satisfy
 * the ad service's full contract (assertBriefComplete, TEN checks — pinned by test against the real gate,
 * never a re-listed copy). The wording and question order are the founder-approved set, lifted from the
 * approved prototype verbatim.
 *
 * Storage rule (founder, 2026-08-01): same question, same column, both surfaces. answersToDiscovery emits
 * ONLY registered discovery keys — an unregistered key would be dropped by the registry with one log line.
 *
 * The channel is NOT asked (founder, 2026-08-01: ads run on Facebook and LinkedIn). No channel hint is
 * stored either: the brief's platform default is already both platforms, and a combined hint string would
 * wrongly narrow it to one (platformMap.toBriefPlatforms matches 'linkedin' first).
 *
 * BROWSER-BUNDLED (MP-07): the discovery form imports PILLS from here, so this file is compiled
 * into both discovery bundles. It MUST stay dependency-free — a require of any node-only module
 * breaks both client-facing builds at build time.
 *
 * @module section-builder/services/teaserQuestions
 */

const PILLS = [
    { key: 'event',   name: 'An event',             look: 'Events',         icon: '🎫' },
    { key: 'product', name: 'A product',            look: 'Social Media',   icon: '📦' },
    { key: 'service', name: 'A service',            look: 'Transformation', icon: '🛠️' },
    { key: 'course',  name: 'A course or training', look: 'Educational',    icon: '🎓' },
    { key: 'news',    name: 'An announcement',      look: 'Announcements',  icon: '📣' }
];

/**
 * The campaign types the ads service accepts, taken FROM the list above so the two cannot drift.
 * An announcement is sent as "news" — there is no value called "announcement".
 */
const ACCEPTED_PROMOTION_TYPES = PILLS.map((p) => p.key);

/**
 * The type to use when a saved discovery is loaded.
 *
 * FOUNDER RULING, 10 August 2026 — this REVERSES decision 19 of 2 August, which offered a retired
 * value back as a sixth option labelled "(previously saved)". A person could pick it again and the
 * request was then refused: that is the 400 seen in production.
 *
 * MEASURED 10 August 2026, our own records: product 28, personal-brand 4, service-business 1, news 1.
 * Two of those the ads service has never accepted.
 *
 * A retired or unknown value is DROPPED. The rest of the discovery still loads; only the type is
 * empty, so the completeness gate names it as the missing answer and the person picks a real one.
 * It is never swapped for another type — nothing maps "personal-brand" onto one of the five, and
 * choosing for them would write a campaign they never asked for.
 */
/**
 * ONE check for "is this discovery finished" — readable by the browser AND the server.
 *
 * MOVED HERE 11 August 2026, founder ruling. It is the SAME rule, moved, not rewritten.
 *
 * It lived on the discovery form and nothing on the server could reach it. The Continue button
 * already used it and used it correctly, per campaign type. The COPY asked a different question — a
 * stored status that is only ever written when a campaign is GENERATED — so a finished discovery
 * still read as unfinished and could not be copied.
 *
 * It sits beside the five campaign types for the same reason they do: this file is compiled into
 * both discovery bundles and required by the server.
 *
 * CONTEXT IS PASSED IN, NEVER GUESSED. Two answers depend on where the form is being filled in: the
 * brand voice is not asked on the external prospect form, and the content type only in NEXUS. A
 * caller states its own context; this check never assumes one.
 *
 * @param {object} d the discovery answers
 * @param {{isNexusMode?: boolean, externalMode?: boolean}} context
 * @returns {{num: number, name: string}|null} the first tab missing a required answer, or null
 */
function firstIncompleteSection(d, { isNexusMode = false, externalMode = false } = {}) {
    d = d || {};
    const t = (v) => !!(v && v.trim && v.trim()) || (!!v && !v.trim);
    if (!d.promotionType) return { num: 0, name: 'Promoting' };
    if (d.promotionType === 'event') {
        // The end time and the host's credential are NOT here on purpose (step 6): the ads
        // service never blocks on either, "from 6pm till late" and drop-in days have no end
        // time, and a first outing has no credential and should not invent one.
        for (const k of ['eventName', 'eventDate', 'eventStartTime', 'eventFormat', 'eventLocation', 'eventHost']) {
            if (!t(d[k])) return { num: 0, name: 'Promoting' };
    }
}
    if (d.promotionType === 'news') {
        if (!t(d.announcementSubject) || !t(d.announcementWhen) || !t(d.announcementImpact)) return { num: 0, name: 'Promoting' };
}
    // ads-brief-per-type-cutover step 5: the ads service refuses a course with no start or
    // teacher, and a service with no area or availability. Finishing must mean it will
    // produce ads, so these block here rather than at the generate step.
    if (d.promotionType === 'course') {
        if (!t(d.courseStarts) || !t(d.courseTeacher)) return { num: 0, name: 'Promoting' };
}
    if (d.promotionType === 'service') {
        if (!t(d.serviceArea) || !t(d.serviceAvailability)) return { num: 0, name: 'Promoting' };
}
    // An ANNOUNCEMENT carries no price and nothing on sale — new opening hours, a move, an
    // award, a rebrand. The ads service asks it for none of this, and demanding it is what
    // stopped those campaigns being finished at all (step 6).
    const news = d.promotionType === 'news';
    // The one-sentence description is required for a product, a course and a service — the
    // ads service asks all three how the thing works. An event and an announcement are not
    // asked it (group E).
    const describes = ['product', 'course', 'service'].includes(d.promotionType);
    // The destination link STOPS BLOCKING — 10 August 2026. The ads service never asks for
    // it, and the cutover document lists it among the answers that must stop being demanded.
    // The form itself labels the field optional and does not mark it required, so the gate
    // was refusing a campaign over a field the person had been told to skip — and because the
    // gate names the first unfinished tab wherever you happen to be standing, it complained
    // about the Offer tab while showing the banner on Brand Voice.
    if (!t(d.productName)) return { num: 1, name: 'Product Info' };
    // Demanded on EVERY type since 11 August 2026 — the ads service requires it
    // unconditionally, so an event or an announcement without one is refused.
    if (!t(d.productDescription)) return { num: 1, name: 'Product Info' };
    // The SAME answer, in the two forms it legitimately takes. On screen it is the word "yes" or "no";
    // in the record it is true or false. Reading only the screen's form is what made a person who
    // answered "no deal" — the common case — read as unfinished, which is precisely the refusal this
    // change exists to end. Answered-at-all and answered-yes are separate questions: "no" is a complete
    // answer, and only "yes" asks for the three deal details.
    const dealAnswered = d.offerHasDeal !== undefined && d.offerHasDeal !== null && d.offerHasDeal !== '';
    const dealOffered = d.offerHasDeal === 'yes' || d.offerHasDeal === true;
    if (!news && (!t(d.productCategory) || !d.pricePoint || !d.purchaseType || !dealAnswered)) return { num: 1, name: 'Product Info' };
    if (dealOffered && (!t(d.offerNormalPrice) || !t(d.offerPrice) || !t(d.offerEnds))) return { num: 1, name: 'Product Info' };
    if (!t(d.targetBuyer) || !d.awarenessLevel || !d.buyerPsychology || !d.purchaseTimeline) return { num: 2, name: 'Audience' };
    // The unique value feeds "how it works", which the ads service asks of every type EXCEPT
    // an announcement — news about a move or an award describes no mechanism (step 6).
    // Everything else here stays required: the landing pages, emails and social content are
    // built from these answers, not only the ads.
    // The customer's own words is NOT here (group E): the ads service never blocks on it, a
    // launch and a first cohort have none, and a regulated provider is barred from naming a
    // client by confidentiality rather than preference.
    // A celebration and a new branch both have nothing going wrong for anyone, so neither an
    // event nor an announcement is asked for a pain.
    // OWNER RULING 2026-08-29: a blank pain answer blocks NOBODY. It was blocking product, course
    // and service; the two lines that did so are deleted, not widened. The question is still asked
    // of everyone and still travels when answered.
    if (!d.keyBenefits?.some(x => x?.trim())) return { num: 3, name: 'Content' };
    if (!news && !t(d.uniqueValue)) return { num: 3, name: 'Content' };
    // The offer detail is the second half of "how it works", and an announcement describes no
    // mechanism — so it stops blocking there exactly as the value proposition does (group E).
    if (!t(d.campaignGoal) || !t(d.callToAction) || !d.conversionDestination) return { num: 4, name: 'Offer' };
    if (!news && !t(d.offerDetails)) return { num: 4, name: 'Offer' };
    if (isNexusMode && !d.contentType) return { num: 4, name: 'Offer' };
    // How hard the ads push is required on every type — the ads service asks every one of
    // them for it (group E).
    if (!t(d.brandTones) || !Number.isInteger(d.adIntensity)) return { num: 5, name: 'Tone' };
    if (!externalMode && !t(d.brandVoice)) return { num: 6, name: 'Brand voice' };
    return null;
}

/** Are all required answers present? The question the Continue button asks. */
function isDiscoveryComplete(d, context) {
    return firstIncompleteSection(d, context) === null;
}

function keepPromotionType(saved) {
    return ACCEPTED_PROMOTION_TYPES.includes(saved) ? saved : '';
}

const BILLING = ['One-off', 'Per month', 'Per year', 'It is free'];
const AWARENESS = [
    { label: 'They know and are looking',          value: 'solution-aware' },
    { label: 'They know but are not looking yet',  value: 'problem-aware' },
    { label: 'We have to show them',               value: 'unaware' }
];
const ACTIONS = ['Book a call', 'Buy now', 'Register', 'Join the waitlist', 'Download it', 'Message us', 'Come to the shop'];
// positioning MP-02 (founder-approved wording, 2026-08-04): the twelve tones, three columns on
// screen, ticks compose comma-joined in tick order into ONE line stored in brandVoice.
const TONES = [
    'direct and practical', 'confident without hype', 'bold and punchy',
    'warm and personal', 'short and to the point', 'calm and reassuring',
    'speaks to one owner', 'expert but plain-spoken', 'premium and polished',
    'no marketing jargon', 'down-to-earth', 'playful, a little cheeky'
];
// The push slider's five stops (founder-approved). 1 is hardest, 5 calmest — the service's scale.
const INTENSITY_STOPS = {
    1: 'All out - big claims, hard urgency, maximum punch',
    2: 'Strong - bold hooks and a clear push to act',
    3: 'Balanced - confident and direct, no hype',
    4: 'Soft - helpful, low pressure',
    5: 'Calm - measured and factual, no push at all'
};

// The grouped openers: one exchange of small labelled facts on the two paths whose look needs them.
const OPENERS = {
    event: {
        id: 'opener', question: 'Tell me about the event.',
        example: 'Spring Growth Workshop. Tuesday 14 October, 7pm to 9pm, in person at The Mill in Leeds. Hosted by Sarah Ellis, who has trained 400 cafe owners.',
        fields: [
            { key: 'eventName',           label: 'Event name' },
            { key: 'eventDate',           label: 'Date' },
            { key: 'eventStartTime',      label: 'Start time' },
            // Never blocks (step 6): "from 6pm till late" and drop-in days have no end time.
            { key: 'eventEndTime',        label: 'End time', min: 0 },
            { key: 'eventFormat',         label: 'In person / online / both', pick: ['In person', 'Online', 'Both'] },
            { key: 'eventLocation',       label: 'Where' },
            { key: 'eventHost',           label: 'Hosted or led by' },
            // Never blocks (step 6): a first outing has none and should not invent one.
            { key: 'eventHostCredential', label: 'Their one-line credential', min: 0 }
        ]
    },
    news: {
        id: 'opener', question: 'Tell me about the news.',
        example: 'We are opening a second branch in Leeds. Doors open on 3 September. It means nobody waits five days for a service any more.',
        fields: [
            { key: 'announcementSubject', label: 'What is changing' },
            { key: 'announcementWhen',    label: 'When it happens or goes live' },
            // Turns a notice into an ad: "we opened a second branch" against "you no longer wait five
            // days". The ads service refuses an announcement without it.
            { key: 'announcementImpact',  label: 'What this changes for them' }
        ]
    },
    course: {
        id: 'opener', question: 'Tell me about the course.',
        example: 'Starts 14 October, four Tuesday evenings. Taught by Sarah Ellis, who has trained 400 cafe owners.',
        fields: [
            // Enrolment closes when a cohort begins — a course's own urgency, and never an offer
            // expiration. "Self-paced" is a complete answer.
            { key: 'courseStarts',  label: 'When it starts, or that it is self-paced' },
            // The teacher carries a course the way a host carries an event.
            { key: 'courseTeacher', label: 'Who teaches it' }
        ]
    },
    service: {
        id: 'opener', question: 'Tell me about the service.',
        example: 'Leeds and 20 miles around it. Same day for emergencies, otherwise booking into March.',
        fields: [
            // Without it the copy cannot say "your local" or name anywhere the reader recognises.
            { key: 'serviceArea',         label: 'The area you cover' },
            // Often the deciding factor, and real urgency with no discount attached.
            { key: 'serviceAvailability', label: 'How soon you can start' }
        ]
    }
};

// The seven shared questions. Same data on every pill; only the words change.
const QUESTIONS = [
    {
        id: 'thing', feeds: 'productDescription',
        wording: {
            event: 'What do you sell to the people who come to the event?',
            product: 'In one sentence, what is the product?',
            service: 'In one sentence, what is the service?',
            course: 'What is the course, and how is it delivered?',
            news: 'What do you sell to the people who will care about this?'
        },
        example: {
            event: 'A 12-week coaching programme for independent cafes.',
            product: 'A cast-iron pan that goes from hob to oven to table.',
            service: 'Bookkeeping catch-up for sole traders who have fallen behind.',
            course: 'A four-week online course on pricing, for freelance designers.',
            news: 'Same-day bike servicing and custom builds.'
        },
        // Founder ruling 2026-08-04: every campaign needs a NAME. Asked here, stored in the name
        // column, and the converted campaign is named with it instead of a made-up label.
        fields: [
            { key: 'productName', label: 'What is it called?', min: 2 }
        ]
    },
    {
        // GROUP D — an announcement describes no mechanism. New opening hours, a move, an award and
        // a rebrand have nothing being sold to explain, so the ads-hooks diagram marks it optional
        // there and required on every other type.
        id: 'how', feeds: 'offerDetails', min: { news: 0 },   // no default: every other type falls through to the standard minimum
        wording: {
            event: 'What actually happens on the day?',
            product: 'How does it actually work?',
            service: 'What do you actually do for them?',
            course: 'Is it taught live, recorded, or in a room?',
            news: 'How does the thing you sell actually work?'
        },
        example: {
            event: 'Three hours. We rebuild your menu pricing live and you leave with your own numbers done.',
            product: 'One piece of cast iron, pre-seasoned, so nothing sticks from the first use.',
            service: 'We deal with the tax office directly so they never have to make the call themselves.',
            course: 'Four live sessions, and you rebuild your own price list in the room with feedback.',
            news: 'Drop it before 10, ride it home by 6, one flat price whatever it needs.'
        }
    },
    {
        id: 'price', feeds: 'pricePoint', min: { news: 0, default: 2 },
        wording: {
            event: 'Is it free, or what does a ticket cost?',
            product: 'What does it cost?', service: 'What does it cost?',
            course: 'What does it cost?', news: 'What does it cost?'
        },
        example: {
            event: '49 pounds a seat, or 80 for two. Early bird ends Friday.',
            product: '78 pounds, on at 59 until Sunday.',
            service: 'From 1,200 pounds, spread over six months.',
            course: '395 pounds.', news: '89 pounds, flat.'
        },
        fields: [
            { key: 'purchaseType',     label: 'How is it charged?', pick: BILLING },
            { key: 'offerHasDeal',     label: 'Is there a deal on it?', pick: ['Yes', 'No'], boolean: true },
            { key: 'offerNormalPrice', label: 'Normal price', when: 'offerHasDeal' },
            { key: 'offerPrice',       label: 'Offer price',  when: 'offerHasDeal' },
            { key: 'offerEnds',        label: 'Ends on',      when: 'offerHasDeal' }
        ]
    },
    {
        id: 'who', feeds: 'targetBuyer', min: 8,
        wording: {
            event: 'Who should be in the room?', product: 'Who buys it today?',
            service: 'Who do you do this for?', course: 'Who is it for?',
            news: 'Who does this matter to most?'
        },
        example: {
            event: 'Independent cafe owners turning over 100,000 to 400,000 a year who have never costed a menu.',
            product: 'People who cook most nights and have replaced two cheap pans already.',
            service: 'Sole traders who stopped opening the post two years ago.',
            course: 'Freelance designers two to five years in, busy and still undercharging.',
            news: 'Commuters who ride every day and cannot lose the bike for a week.'
        }
    },
    {
        // The event and announcement diagrams both put three questions in their "being replaced —
        // not asked again" band, NOT the optional band: the pain, what it is costing them, and the
        // period. A celebration has nothing going wrong; a new branch prices no problem.
        // `pills` here names the types asked this question's OWN answer; the fields below are
        // filtered separately, so both are still asked whether they know it is a problem.
        // OWNER RULING 2026-08-29: asked on ALL FIVE types. The wording and worked examples below
        // already existed for an event and an announcement; only the answer box was withheld, so
        // those two read the question and had nowhere to answer it. The recorded reason — "a
        // celebration has nothing going wrong; a new branch prices no problem" — holds for a
        // celebration, not for a business event that sells on a problem. A celebration leaves it
        // blank; a business event fills it in.
        id: 'problem', feeds: 'painPoints', array: true,
        wording: {
            event: 'What is going wrong for them that brings them to something like this?',
            product: 'What is going wrong for them right now?',
            service: 'What is going wrong for them when they first come to you?',
            course: 'What do most people get wrong before they learn this?',
            news: 'What is going wrong for them today?'
        },
        example: {
            event: 'They are busy every day and somehow making nothing. They cannot see where it goes.',
            product: 'Everything sticks, and they have thrown away two pans in three years.',
            service: 'Three years of unfiled accounts and a drawer of letters they have not opened.',
            course: 'They price by the hour, so getting faster cuts their own income.',
            news: 'Every shop keeps the bike five days and they end up on the bus.'
        },
        fields: [
            // GROUP B — never blocks. It only exists when the reader already pays for something.
            // A networking evening and a piece of news price no problem, and demanding a figure is
            // where one gets invented.
            { key: 'problemCost',       label: 'What is that costing them right now?', min: 0 },
            // Follows the figure, the way the deal boxes follow the deal. Never asked on its own.
            { key: 'problemCostPeriod', label: 'Per week, per month, or per year?', pick: ['Per week', 'Per month', 'Per year'], when: 'problemCost' },
            { key: 'awarenessLevel',    label: 'Do they already know it is a problem?', pick: AWARENESS },
            // The ads-hooks contract places this on the PRODUCT path and no other, and never blocks.
            { key: 'topObjection',      label: 'The objection you hear most', min: 0, pills: ['product'] }
        ]
    },
    {
        // GROUP A — the whole proof screen never blocks. A launch has none, a first-run event has no
        // past attendee, a first cohort has no graduate, and a regulated provider is barred from
        // naming a client by confidentiality rather than by preference.
        id: 'proof', feeds: 'proofNumber', min: 0,
        wording: {
            event: 'What proof have you got?', product: 'What proof have you got?',
            service: 'What proof have you got?', course: 'What proof have you got?',
            news: 'What proof have you got?'
        },
        example: {
            event: 'We have run it 14 times, average 38 in the room. Marcus, who runs two delis: "I put prices up the next morning and nobody blinked."',
            product: '11,000 sold, 2.4 percent returned. Ellie, cooks for four: "I have not used another pan since."',
            service: '61 clients brought current in two years. Marcus, a joiner: "cleared 14,000 pounds of arrears in seven months."',
            course: '212 people through it, average rate up 34 percent. Amara, brand designer: "I doubled my fee and nobody argued."',
            news: '6,000 services in four years, 94 percent same day. Dan, a courier: "dropped it at nine, working again by five."'
        },
        fields: [
            { key: 'socialProof',    label: 'A customer in their own words', min: 0 },
            { key: 'proofQuoteName', label: 'Their first name', min: 0 },
            { key: 'proofQuoteRole', label: 'What they do', min: 0 }
        ]
    },
    {
        id: 'positioning',
        wording: {
            event: 'Nearly done: how should your ads sound?', product: 'Nearly done: how should your ads sound?',
            service: 'Nearly done: how should your ads sound?', course: 'Nearly done: how should your ads sound?',
            news: 'Nearly done: how should your ads sound?'
        },
        example: {
            event: 'Tick the tones that sound like you, add your own words if you like, and set how hard the ads push.',
            product: 'Tick the tones that sound like you, add your own words if you like, and set how hard the ads push.',
            service: 'Tick the tones that sound like you, add your own words if you like, and set how hard the ads push.',
            course: 'Tick the tones that sound like you, add your own words if you like, and set how hard the ads push.',
            news: 'Tick the tones that sound like you, add your own words if you like, and set how hard the ads push.'
        },
        fields: [
            // tones: a checkbox-compose control — ticks join comma-separated in tick order; the
            // freehand appends, or stands alone. Empty is refused: the send requires a voice.
            { key: 'brandTones', label: 'Pick at least one, or write your own.', tones: TONES, moreLabel: 'More, in your own words', min: 1 },
            // the push: a REAL number, one to five, default three. The browser posts a number and it
            // stays a number end to end (founder ruling — no conversion after the fact).
            { key: 'adIntensity', label: 'How hard should your ads push?', slider: { min: 1, max: 5, default: 3, stops: INTENSITY_STOPS } }
        ]
    },
    {
        id: 'action', feeds: 'conversionDestination', isPick: true,
        wording: {
            event: 'When someone clicks the ad, what should happen, and where does it take them?',
            product: 'When someone clicks the ad, what should happen, and where does it take them?',
            service: 'When someone clicks the ad, what should happen, and where does it take them?',
            course: 'When someone clicks the ad, what should happen, and where does it take them?',
            news: 'When someone clicks the ad, what should happen, and where does it take them?'
        },
        example: {
            event: 'Pick one, then paste the link it goes to.', product: 'Pick one, then paste the link it goes to.',
            service: 'Pick one, then paste the link it goes to.', course: 'Pick one, then paste the link it goes to.',
            news: 'Pick one, then paste the link it goes to.'
        },
        pick: ACTIONS,
        fields: [{ key: 'actionUrl', label: 'And where does it take them?', url: true, min: { event: 0, news: 0, service: 0, default: 1 } }]
    }
];

/**
 * Resolve a `min` that may differ by campaign type. A number applies to every type. An object is
 * keyed by type, with `default` for the rest: `{ news: 0, default: 2 }`.
 *
 * The ads-hooks diagrams require this. The destination link is required for a product and a course
 * and optional for the other three; the price is required for a product and optional for an
 * announcement. One number for every type cannot express either.
 */
function _minFor(min, pillKey) {
    if (min === null || min === undefined) return undefined;
    if (typeof min === 'number') return min;
    return min[pillKey] !== undefined ? min[pillKey] : min.default;
}

/**
 * The ordered question list for one pill: the grouped opener (event/news/course/service), then the
 * shared questions — with every `min` resolved for this type and every field the type does not ask
 * removed.
 *
 * Everything downstream reads this: the answer gate, the per-screen save, the final mapping and the
 * rendered screens. Resolving here is what keeps them from disagreeing.
 */
function forPill(pillKey) {
    if (!PILLS.some((p) => p.key === pillKey)) throw new Error(`teaserQuestions: unknown pill "${pillKey}"`);
    const opener = OPENERS[pillKey];
    const all = opener ? [opener, ...QUESTIONS] : [...QUESTIONS];
    return all.map((q) => {
        const resolved = { ...q };
        // `pills` on the question names the ONLY types asked its own answer. The screen renders no
        // main box without `feeds` (TeaserConversation), so the fields below still stand on their own.
        if (q.pills && !q.pills.includes(pillKey)) { delete resolved.feeds; delete resolved.array; }
        if (q.min !== undefined) resolved.min = _minFor(q.min, pillKey);
        if (q.fields) {
            resolved.fields = q.fields
                // `pills` names the ONLY types that ask this field. Absent means every type asks it.
                .filter((f) => !f.pills || f.pills.includes(pillKey))
                .map((f) => (f.min === undefined ? f : { ...f, min: _minFor(f.min, pillKey) }));
        }
        return resolved;
    });
}

/**
 * Map one pill's answers → the discovery shape (camelCase registry keys only). The pill itself is stored
 * as promotionType — it decides the look and is business data, never inferred from which fields are filled.
 * Empty answers are omitted, never emitted as blanks.
 */
function answersToDiscovery(pillKey, answers = {}) {
    if (!PILLS.some((p) => p.key === pillKey)) throw new Error(`teaserQuestions: unknown pill "${pillKey}"`);
    const out = { promotionType: pillKey };
    const put = (key, value, asArray) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'string' && value.trim() === '') return;
        // A list answer is split on line breaks, so a returning client's multi-item list survives the
        // round trip instead of collapsing into a single blob (review: the column must stay a real list).
        out[key] = asArray
            ? String(value).split('\n').map((v) => v.trim()).filter(Boolean)
            : (typeof value === 'boolean' || typeof value === 'number' ? value : String(value).trim());
    };
    for (const q of forPill(pillKey)) {
        if (q.feeds) put(q.feeds, answers[q.feeds], q.array === true);
        for (const f of q.fields || []) {
            // The same rule the answer gate applies: a conditional field exists only when its condition
            // is met. Without this skip, a hand-made post could record a deal price against "no deal".
            if (!_conditionMet(q, f, answers)) continue;
            put(f.key, answers[f.key], false);
        }
    }
    return out;
}

const MIN_ANSWER = 12; // characters, main text answers — stated on the page before the person types

/**
 * The server-side answer gate. Runs BEFORE the single-use pass is consumed, so a refusal never costs the
 * prospect their link. Mirrors the page's own rules exactly: main answers carry a minimum length, picks
 * must be picked, the deal's detail fields are required only when there IS a deal, and a path's grouped
 * facts are required on that path only.
 * @returns {{ok:boolean, missing:string[]}}
 */
const _text = (v) => (typeof v === 'string' ? v.trim() : '');

/**
 * Is a conditional field's condition met?
 *
 * Two kinds of question gate a follow-up, and they answer in different shapes. A yes/no question
 * stores a real boolean, so only a YES reveals its detail boxes. A typed question stores text, and
 * its follow-up exists the moment anything is typed.
 *
 * Reading both as "must be exactly true" is what kept the cost's period permanently out of reach:
 * a typed figure is a string, never the boolean true, so the period was skipped everywhere and its
 * column could only ever be written empty. The gating field's own definition decides which rule
 * applies, so neither kind has to be listed by name.
 */
function _conditionMet(q, f, answers = {}) {
    if (!f.when) return true;
    const gate = (q.fields || []).find((x) => x.key === f.when);
    if (gate && gate.boolean) return answers[f.when] === true;
    return _text(answers[f.when]) !== '';
}

/** The per-question rules — ONE set, used by the full gate and the per-screen save alike. */
function judgeQuestion(q, answers, missing) {
    const text = _text;
    {
        // Per-field-kind minimums (review ruling): a price of "78" or a proof of "94%" is a legitimate
        // answer; only composition answers carry the full minimum. A pick needs presence, never length.
        const min = q.isPick ? 1 : (q.min !== undefined ? q.min : MIN_ANSWER);
        if (q.feeds && text(answers[q.feeds]).length < min) missing.push(q.feeds);
        for (const f of q.fields || []) {
            if (!_conditionMet(q, f, answers)) continue;   // conditional: only when its own question was answered
            if (f.boolean) {
                if (answers[f.key] !== true && answers[f.key] !== false) missing.push(f.key);
            } else if (f.pick) {
                if (!text(answers[f.key])) missing.push(f.key);
            } else if (f.slider) {
                // A NUMBER one to five, exactly as the browser's slider posts it (founder ruling).
                const n = answers[f.key];
                if (!Number.isInteger(n) || n < f.slider.min || n > f.slider.max) missing.push(f.key);
            } else if (f.url) {
                // Prospect-supplied destination: a proper https address or refused by name, at the door,
                // while the person can still fix it — never stored unchecked for a later block to trust.
                //
                // A minimum of 0 makes the link optional for this campaign type, as the diagrams do for
                // an event, an announcement and a service. Optional means it may be LEFT BLANK — an
                // address that IS supplied is still checked, so nothing unverified ever gets stored.
                const supplied = text(answers[f.key]);
                if (!supplied && f.min === 0) continue;
                if (!/^https:\/\//i.test(supplied)) missing.push(f.key);
            } else if (text(answers[f.key]).length < (f.min !== undefined ? f.min : 1)) {
                missing.push(f.key);
            }
        }
        if (q.pick && !text(answers.conversionDestination)) missing.push('conversionDestination');
    }
}

function validateAnswers(pillKey, answers = {}) {
    const missing = [];
    for (const q of forPill(pillKey)) judgeQuestion(q, answers, missing);
    return { ok: missing.length === 0, missing: [...new Set(missing)] };
}

/**
 * Judge ONE screen by the same rules (the elimination ruling, founder 2026-08-04): the save that
 * runs as the person advances uses THIS, so nothing invalid can ever pass a save.
 */
function validateQuestion(pillKey, questionId, answers = {}) {
    const q = forPill(pillKey).find((x) => x.id === questionId);
    if (!q) throw new Error(`teaserQuestions: unknown question "${questionId}" on pill "${pillKey}"`);
    const missing = [];
    judgeQuestion(q, answers, missing);
    return { ok: missing.length === 0, missing: [...new Set(missing)] };
}

/**
 * Map ONE screen's answers to registry keys for a per-screen save. Switched-off conditional fields
 * are cleared EXPLICITLY (null) so a correction can never leave stale data behind — absent is not
 * enough when the row already holds a value.
 */
function questionToDiscovery(pillKey, questionId, answers = {}) {
    const q = forPill(pillKey).find((x) => x.id === questionId);
    if (!q) throw new Error(`teaserQuestions: unknown question "${questionId}" on pill "${pillKey}"`);
    const out = { promotionType: pillKey };
    const put = (key, value, asArray) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'string' && value.trim() === '') return;
        out[key] = asArray
            ? String(value).split('\n').map((v) => v.trim()).filter(Boolean)
            : (typeof value === 'boolean' || typeof value === 'number' ? value : String(value).trim());
    };
    if (q.feeds) put(q.feeds, answers[q.feeds], q.array === true);
    for (const f of q.fields || []) {
        if (!_conditionMet(q, f, answers)) { out[f.key] = null; continue; }
        put(f.key, answers[f.key], false);
    }
    if (q.pick) put('conversionDestination', answers.conversionDestination, false);
    return out;
}

module.exports = { PILLS, ACCEPTED_PROMOTION_TYPES, keepPromotionType, firstIncompleteSection, isDiscoveryComplete, QUESTIONS, OPENERS, TONES, INTENSITY_STOPS, forPill, answersToDiscovery, validateAnswers, validateQuestion, questionToDiscovery, MIN_ANSWER };
